import React, { useState, useEffect } from "react";
import OptimizedImage from "@/components/OptimizedImage";
import ReactPlayer from "react-player";



type VideoSource = {
  url: string;
  type: "youtube" | "vimeo" | "facebook" | "instagram" | "mp4";
  duration?: string;
  title?: string;
  thumbnail?: string;
  aspect?: "horizontal" | "vertical";
}

 

type ArtistVideoPlayerProps = {
  source: VideoSource;
  profile_pic: string;
  isThumbnail?: boolean;
  onClick?: () => void;
  containerClassName?: string;
  imageClassName?: string;
  overlayClassName?: string;
  durationClassName?: string;
  titleClassName?: string;
  playButton?: React.ReactNode;
  isPlaying?: boolean;
  onPlay?: () => void;
  setIsPlaying?: any;
};

const ArtistVideoPlayer = ({
  source,
  profile_pic,
  isThumbnail = false,
  onClick,
  containerClassName = "relative aspect-video bg-black",
  imageClassName = "object-contain",
  overlayClassName = "absolute inset-0 bg-black/40 flex items-center justify-center",
  durationClassName = "absolute bottom-2 right-2 max-md:top-2 max-md:left-2 text-sm text-white font-medium",
  titleClassName = "absolute top-2 left-2 max-md:hidden text-sm text-white font-medium",
  playButton,
  isPlaying,
  onPlay,
  setIsPlaying,
}: ArtistVideoPlayerProps) => {
  const [metadata, setMetadata] = useState<VideoSource>(source);
  const [showFallback, setShowFallback] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchMetadata = async () => {
      try {
        let response, data;
        const url = source.url;

        switch (source.type) {
          case "youtube":
          case "vimeo": {
            const apiUrl =
              source.type === "youtube"
                ? `https://noembed.com/embed?url=${url}`
                : `https://vimeo.com/api/oembed.json?url=${url}`;

            response = await fetch(apiUrl);
            if (!response.ok) throw new Error("Failed to fetch video metadata");

            data = await response.json();
            
            break;
          }

          case "instagram": {
            response = await fetch(`https://noembed.com/embed?url=${url}`);
            if (!response.ok)
              throw new Error("Failed to fetch Instagram metadata");

            data = await response.json();
            break;
          }

          case "facebook": {
            data = { title: source.title, thumbnail_url: source.thumbnail };
            break;
          }

          case "mp4": {
            if (typeof window !== "undefined") {
              const video = document.createElement("video");
              video.src = url;
              video.preload = "metadata";

              video.onloadedmetadata = () => {
                if (isMounted) {
                  setMetadata({
                    title: `${video.src.split("/").at(-1)?.split(".").at(0)} Video`,
                    duration: formatDuration(video.duration),
                    width: video.videoWidth,
                    height: video.videoHeight,
                    url: video.currentSrc,
                  });
                }
              };
            }
            return; // Exit to prevent redundant setMetadata() call
          }

          default:
            throw new Error("Unsupported source type");
        }

        // Ensure state is only updated if component is still mounted
        if (isMounted) {
          setMetadata((prev) => ({
            ...prev,
            title: data?.title || prev.title,
            thumbnail: data?.thumbnail_url || prev.thumbnail,
            duration: data?.duration
              ? formatDuration(data.duration)
              : prev.duration,
          }));
        }
      } catch (error) {
        console.error("Error fetching metadata:", error);
        if (isMounted) setShowFallback(true);
      }
    };

    if ((!metadata.thumbnail || !metadata.duration) && source.url) {
      fetchMetadata();
    }

    return () => {
      isMounted = false; // Cleanup function to prevent state update on unmounted component
    };
  }, [source.url, source.type]);

  const DefaultPlayButton = ({ size }: { size?: number }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className="size-24 max-md:size-16 text-white/90 hover:text-white transition-all"
    >
      <path fill="none" d="M0 0h24v24H0z" />
      <path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM10.622 8.415l4.879 3.252a.4.4 0 0 1 0 .666l-4.88 3.252a.4.4 0 0 1-.621-.332V8.747a.4.4 0 0 1 .622-.332z" />
    </svg>
  );

  if (isThumbnail) {
    return (
      <div className={`cursor-pointer ${containerClassName}`} onClick={onClick}>
        <div className="relative w-full h-full">
          <OptimizedImage
            src={metadata.thumbnail || profile_pic}
            alt={metadata.title || "Video thumbnail"}
            fill
            className={`${imageClassName} ${!metadata.thumbnail && "blur-sm opacity-75"}`}
            onError={() => setShowFallback(true)}
          />
          <div className={overlayClassName}>
            {playButton || <DefaultPlayButton />}
            {metadata.title && (
              <span className={titleClassName}>{metadata.title}</span>
            )}
            {metadata.duration && (
              <span className={durationClassName}>{metadata.duration}</span>
            )}
          </div>
        </div>
      </div>
    );
  }

  switch (source.type) {
    case "facebook":
      return (
        <div className="w-full h-full">
          <iframe
            src={`https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(
              source.url
            )}&show_text=false&height=281&autoplay=true`}
            width="100%"
            height="100%"
            style={{ border: "none", overflow: "hidden" }}
            scrolling="no"
            frameBorder="0"
            allowFullScreen
            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share; controls"
          ></iframe>
        </div>
      );

    default:
      return (
        <div className="relative aspect-video bg-black">
          <ReactPlayer
            url={source.url}
            light={
              !isPlaying &&
              (showFallback ? (
                profile_pic
              ) : (
                <div className="relative w-full h-full cursor-pointer">
                  <OptimizedImage
                    src={metadata.thumbnail || profile_pic}
                    alt={metadata.title || "Video thumbnail"}
                    fill
                    className="object-contain"
                    onError={() => setShowFallback(true)}
                  />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <DefaultPlayButton />
                    {metadata.title && (
                      <span className="absolute top-4 left-4 text-xl max-sm:text-base text-white font-medium">
                        {metadata.title}
                      </span>
                    )}
                    {metadata.duration && metadata.duration === "NaN:NaN" && (
                      <span className="absolute bottom-4 right-4 text-xl text-white font-medium">
                        {metadata.duration}
                      </span>
                    )}
                  </div>
                </div>
              ))
            }
            onClickPreview={onClick || (() => setIsPlaying(true))}
            width="100%"
            height="100%"
            style={{
              objectFit: "contain",
            }}
            controls
            config={{
              file: {
                attributes: { controlsList: "nodownload" },
                forceVideo: true,
              },
            }}
            onError={() => setShowFallback(true)}
          />
        </div>
      );
  }
};

const formatDuration = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

export default ArtistVideoPlayer;